import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  Cloud,
  Folder,
  Loader2,
  ChevronRight,
  ChevronDown,
  FileText,
  Image,
  File,
  ArrowLeft,
  Check,
  Grid3X3,
  List,
} from "lucide-react";
import googleDriveService, {
  GoogleDriveFile,
} from "../../services/google-drive.service";
import Loader from "../../widgets/Loader";
import DropboxConnect from "./DropboxConnect";
import OnedriveConnect from "./OnedriveConnect";

interface CloudStorageConnectProps {
  storageType: "GOOGLE DRIVE" | "DROPBOX" | "ONEDRIVE";
  googleDriveToken: string | null;
  setGoogleDriveToken: (token: string | null) => void;
  dropboxToken: string | null;
  setDropboxToken: (token: string | null) => void;
  onedriveToken: string | null;
  setOnedriveToken: (token: string | null) => void;
  onUploadSuccess?: () => void;
  documentStoreId?: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

const CloudStorageConnect: React.FC<CloudStorageConnectProps> = ({
  storageType,
  googleDriveToken,
  setGoogleDriveToken,
  dropboxToken,
  setDropboxToken,
  onedriveToken,
  setOnedriveToken,
  onUploadSuccess,
  documentStoreId,
}) => {
  const [googleDriveFolders, setGoogleDriveFolders] =
    useState<GoogleDriveFile | null>(null);
  const [isLoadingGDriveFolders, setIsLoadingGDriveFolders] = useState(false);
  const [selectedGDriveFolder, setSelectedGDriveFolder] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [currentFolder, setCurrentFolder] = useState<GoogleDriveFile | null>(
    null
  );
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: "root", name: "My Drive" },
  ]);
  const [isUploading, setIsUploading] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "tree">("grid");
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set()
  );

  // Fetch Google Drive folders when token is available
  useEffect(() => {
    if (googleDriveToken && storageType === "GOOGLE DRIVE") {
      fetchGoogleDriveFolders();
    }
  }, [googleDriveToken, storageType]);

  const handleGoogleDriveAuth = async () => {
    window.open(
      `${import.meta.env.VITE_DMS_API}/dms-v2-srv/google-drive-file-storage-srv/auth/google`,
      "_blank"
    );
  };

  const fetchGoogleDriveFolders = async () => {
    if (!googleDriveToken) return;

    setIsLoadingGDriveFolders(true);
    try {
      const response = await googleDriveService.listFiles(googleDriveToken);
      if (response.success) {
        setGoogleDriveFolders(response.data);
        setCurrentFolder(response.data);
      } else if (response.code === 500) {
        toast.error("Token expired. Please reconnect to Google Drive");
        localStorage.removeItem("google_drive_token");
        setGoogleDriveToken(null);
        setGoogleDriveFolders(null);
      }
    } catch (error: any) {
      console.error("Error fetching Google Drive folders:", error);
      toast.error("Failed to fetch Google Drive folders, please reconnect");
      localStorage.removeItem("google_drive_token");
      setGoogleDriveToken(null);
      setGoogleDriveFolders(null);
      setSelectedGDriveFolder(null);
    } finally {
      setIsLoadingGDriveFolders(false);
    }
  };

  const handleGDriveFolderSelect = (folderId: string, folderName: string) => {
    if (selectedGDriveFolder?.id === folderId) {
      setSelectedGDriveFolder(null);
    } else {
      setSelectedGDriveFolder({ id: folderId, name: folderName });
    }
  };

  const handleFolderNavigate = (folder: GoogleDriveFile) => {
    if (folder.mimeType === "application/vnd.google-apps.folder") {
      setCurrentFolder(folder);
      setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
      setSelectedGDriveFolder(null);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);

    if (index === 0) {
      setCurrentFolder(googleDriveFolders);
    } else {
      // Navigate through folder structure
      let folder = googleDriveFolders;
      for (let i = 1; i <= index; i++) {
        const targetId = newBreadcrumbs[i].id;
        folder = findFolderById(folder, targetId);
        if (!folder) break;
      }
      setCurrentFolder(folder);
    }
    setSelectedGDriveFolder(null);
  };

  const findFolderById = (
    folder: GoogleDriveFile | null,
    targetId: string
  ): GoogleDriveFile | null => {
    if (!folder) return null;
    if (folder.id === targetId) return folder;

    if (folder.children) {
      for (const child of folder.children) {
        if (child.id === targetId) return child;
        const found = findFolderById(child, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const handleUploadFolder = async () => {
    if (!selectedGDriveFolder || !googleDriveToken) return;

    setIsUploading(true);
    try {
      await googleDriveService.uploadFolder({
        access_token: googleDriveToken,
        folder_id: selectedGDriveFolder.id,
        folder_name: selectedGDriveFolder.name,
        upload_type: "GOOGLE DRIVE",
      });

      toast.success(
        `Folder "${selectedGDriveFolder.name}" uploaded successfully!`
      );
      setSelectedGDriveFolder(null);

      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error: any) {
      console.error("Error uploading folder:", error);
      if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to upload folder");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType === "application/vnd.google-apps.folder") {
      return <Folder className="h-8 w-8 text-yellow-500" />;
    } else if (mimeType.startsWith("image/")) {
      return <Image className="h-8 w-8 text-purple-500" />;
    } else if (mimeType.includes("document") || mimeType.includes("text")) {
      return <FileText className="h-8 w-8 text-blue-600" />;
    }
    return <File className="h-8 w-8 text-gray-500" />;
  };

  const getCurrentFolderItems = (): GoogleDriveFile[] => {
    if (!currentFolder || !currentFolder.children) return [];
    return currentFolder.children;
  };

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const renderTreeView = (
    folder: GoogleDriveFile,
    level: number = 0
  ): JSX.Element | null => {
    const isFolder = folder.mimeType === "application/vnd.google-apps.folder";
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = selectedGDriveFolder?.id === folder.id;

    return (
      <div key={folder.id} className="select-none">
        {level > 0 && (
          <>
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all group ${
                isFolder
                  ? `cursor-pointer hover:bg-blue-50 ${
                      isSelected
                        ? "bg-blue-100 border border-blue-300"
                        : "border border-transparent"
                    }`
                  : "border border-transparent opacity-60"
              }`}
              style={{ marginLeft: `${(level - 1) * 20}px` }}
            >
              {/* Expand/Collapse Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasChildren && isFolder) toggleFolder(folder.id);
                }}
                className={`flex-shrink-0 p-0.5 rounded hover:bg-gray-200 transition-colors ${
                  !hasChildren || !isFolder ? "invisible" : ""
                }`}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-600" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                )}
              </button>

              {/* Radio Button for Folders Only */}
              {isFolder ? (
                <input
                  type="radio"
                  name="gdrive-folder-selection"
                  checked={isSelected}
                  onChange={() =>
                    handleGDriveFolderSelect(folder.id, folder.name)
                  }
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-blue-600 bg-white border-gray-300 focus:ring-blue-500 focus:ring-2 cursor-pointer flex-shrink-0"
                />
              ) : (
                <div className="w-4 h-4 flex-shrink-0" />
              )}

              {/* Folder/File Icon */}
              <div className="flex-shrink-0">
                {getFileIcon(folder.mimeType)}
              </div>

              {/* Folder/File Name */}
              <span
                className={`text-sm flex-1 truncate ${
                  isFolder
                    ? isSelected
                      ? "text-gray-900 font-semibold"
                      : "text-gray-700 group-hover:text-gray-900 font-medium"
                    : "text-gray-600"
                }`}
                onClick={() => {
                  if (isFolder) {
                    handleGDriveFolderSelect(folder.id, folder.name);
                  }
                }}
              >
                {folder.name}
              </span>

              {/* File size for files */}
              {!isFolder && folder.size && (
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {(parseInt(folder.size) / 1024).toFixed(2)} KB
                </span>
              )}
            </div>

            {/* Children */}
            {hasChildren && isExpanded && (
              <div className="mt-0.5">
                {folder.children?.map((child) =>
                  renderTreeView(child, level + 1)
                )}
              </div>
            )}
          </>
        )}

        {level === 0 &&
          folder.children &&
          folder.children.map((child) => renderTreeView(child, 1))}
      </div>
    );
  };

  // Render content based on storage type
  const renderContent = () => {
    switch (storageType) {
      case "GOOGLE DRIVE":
        if (!googleDriveToken) {
          // Not Authorized - Show Authorization UI
          return (
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200 mt-3">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Cloud className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 leading-tight">
                  Connect to Google Drive
                </h3>
                <p className="text-xs text-gray-600 leading-tight mt-1">
                  Authorize access to import folders from your Google Drive
                  account
                </p>
              </div>
              <button
                onClick={handleGoogleDriveAuth}
                className="flex-shrink-0 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
              >
                <Cloud className="w-4 h-4" />
                Connect
              </button>
            </div>
          );
        } else {
          // Authorized - Show Google Drive-like UI
          const items = getCurrentFolderItems();
          const folders = items.filter(
            (item) => item.mimeType === "application/vnd.google-apps.folder"
          );
          const files = items.filter(
            (item) => item.mimeType !== "application/vnd.google-apps.folder"
          );

          return (
            <div className="flex flex-col bg-white rounded-lg border border-gray-200">
              {/* Toolbar */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Back Button - Only for Grid View */}
                  {viewMode === "grid" && breadcrumbs.length > 1 && (
                    <button
                      onClick={() =>
                        handleBreadcrumbClick(breadcrumbs.length - 2)
                      }
                      className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                      title="Go back"
                    >
                      <ArrowLeft className="h-4 w-4 text-gray-600" />
                    </button>
                  )}

                  {/* Breadcrumbs - Only for Grid View */}
                  {viewMode === "grid" && (
                    <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto">
                      {breadcrumbs.map((crumb, index) => (
                        <React.Fragment key={crumb.id}>
                          {index > 0 && (
                            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                          <button
                            onClick={() => handleBreadcrumbClick(index)}
                            className={`text-sm font-medium transition-colors flex-shrink-0 ${
                              index === breadcrumbs.length - 1
                                ? "text-gray-900"
                                : "text-gray-600 hover:text-gray-900"
                            }`}
                          >
                            {crumb.name}
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {/* Title for Tree View */}
                  {viewMode === "tree" && (
                    <div className="text-sm font-medium text-gray-900">
                      My Drive
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Selected Folder Info */}
                  {selectedGDriveFolder && (
                    <div className="text-xs text-gray-600 mr-2 hidden md:block">
                      <span className="font-medium">Selected:</span>{" "}
                      <span className="text-gray-900">
                        {selectedGDriveFolder.name}
                      </span>
                    </div>
                  )}

                  {/* Import Button */}
                  <button
                    onClick={handleUploadFolder}
                    disabled={!selectedGDriveFolder || isUploading}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
                    title={
                      selectedGDriveFolder
                        ? "Import selected folder"
                        : "Select a folder to import"
                    }
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Importing...</span>
                      </>
                    ) : (
                      <>
                        <Cloud className="w-3.5 h-3.5" />
                        <span>Import</span>
                      </>
                    )}
                  </button>

                  {/* View Toggle */}
                  <div className="flex items-center rounded-lg border border-gray-300 p-0.5">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-1.5 rounded transition-colors ${
                        viewMode === "grid"
                          ? "bg-blue-100 text-blue-600"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                      title="Grid view"
                    >
                      <Grid3X3 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setViewMode("tree")}
                      className={`p-1.5 rounded transition-colors ${
                        viewMode === "tree"
                          ? "bg-blue-100 text-blue-600"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                      title="Tree view"
                    >
                      <List className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Disconnect Button */}
                  <button
                    onClick={() => {
                      localStorage.removeItem("google_drive_token");
                      setGoogleDriveToken(null);
                      setGoogleDriveFolders(null);
                      setSelectedGDriveFolder(null);
                      setCurrentFolder(null);
                      setBreadcrumbs([{ id: "root", name: "My Drive" }]);
                    }}
                    className="text-xs text-red-600 hover:text-red-700 font-medium px-3 py-1.5 rounded-md hover:bg-red-50 transition-colors flex-shrink-0"
                  >
                    Disconnect
                  </button>
                </div>
              </div>

              {/* Description Header */}
              {!isLoadingGDriveFolders && (
                <div className="px-4 pt-4 pb-2">
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Folder className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">
                        Browse Your Google Drive
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {viewMode === "grid"
                          ? "These are the folders available in your Google Drive. Click to select a folder, then click the folder icon to open it, or use the 'Import Folder' button to import."
                          : "These are the folders available in your Google Drive. Expand folders using the arrow, select with the radio button, then click 'Import Folder' to import."}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Content Area */}
              <div className="px-4 pb-4 min-h-[350px] max-h-[450px] overflow-y-auto">
                {isLoadingGDriveFolders ? (
                  <div className="flex flex-col items-center justify-center h-full gap-1 mt-[90px]">
                    <Loader />
                    <p className="text-sm text-gray-600 font-medium">
                      Loading your Google Drive folders...
                    </p>
                    <p className="text-xs text-gray-500">
                      This may take a moment
                    </p>
                  </div>
                ) : viewMode === "tree" ? (
                  // Tree View
                  googleDriveFolders ? (
                    <div className="space-y-1">
                      {renderTreeView(googleDriveFolders, 0)}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center py-12">
                      <Folder className="w-16 h-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 text-sm">No items found</p>
                    </div>
                  )
                ) : items.length > 0 ? (
                  // Grid View
                  <div className="space-y-4">
                    {/* Folders Section */}
                    {folders.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          Folders
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                          {folders.map((folder) => (
                            <div
                              key={folder.id}
                              onClick={() =>
                                handleGDriveFolderSelect(folder.id, folder.name)
                              }
                              className={`relative group cursor-pointer rounded-lg border-2 p-3 transition-all ${
                                selectedGDriveFolder?.id === folder.id
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                              }`}
                            >
                              {/* Selection Indicator */}
                              {selectedGDriveFolder?.id === folder.id && (
                                <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                                  <Check className="h-3.5 w-3.5 text-white" />
                                </div>
                              )}

                              {/* Open Folder Button - Top Right */}
                              {folder.children &&
                                folder.children.length > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFolderNavigate(folder);
                                    }}
                                    className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-all hover:bg-blue-50 hover:border-blue-300"
                                    title="Open folder"
                                  >
                                    <ChevronRight className="h-3.5 w-3.5 text-blue-600" />
                                  </button>
                                )}

                              {/* Folder Icon */}
                              <div className="flex flex-col items-center gap-2">
                                {getFileIcon(folder.mimeType)}
                                <span className="text-xs text-center text-gray-700 font-medium line-clamp-2 w-full">
                                  {folder.name}
                                </span>
                              </div>

                              {/* Subfolder Count Badge */}
                              {folder.children &&
                                folder.children.length > 0 && (
                                  <div className="absolute bottom-2 left-2 px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600 font-medium">
                                    {folder.children.length} items
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Files Section */}
                    {files.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          Files ({files.length})
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                          {files.map((file) => (
                            <div
                              key={file.id}
                              className="flex flex-col items-center gap-1 p-2 rounded-lg border border-gray-200 bg-gray-50 opacity-60"
                            >
                              {getFileIcon(file.mimeType)}
                              <span className="text-xs text-center text-gray-600 line-clamp-2 w-full">
                                {file.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <Folder className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-sm">
                      No items found in this folder
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        }

      case "ONEDRIVE":
        return (
          <OnedriveConnect
            onedriveToken={onedriveToken}
            setOnedriveToken={setOnedriveToken}
            onUploadSuccess={onUploadSuccess}
            documentStoreId={documentStoreId}
          />
        );

      case "DROPBOX":
        return (
          <DropboxConnect
            dropboxToken={dropboxToken}
            setDropboxToken={setDropboxToken}
            onUploadSuccess={onUploadSuccess}
            documentStoreId={documentStoreId}
          />
        );

      default:
        return null;
    }
  };

  return <div className="bg-white">{renderContent()}</div>;
};

export default CloudStorageConnect;
